import React, { useState } from "react";
import { ArrowLeft, Eye, EyeOff, Lock, MessageSquareText, Phone, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthShell } from "@packages/ui";
import { SmsCodePurpose } from "@packages/global";
import { useAuthStore } from "../../stores/authStore";
import { appConfig } from "../../config/app.config";
import ThemeToggle from "../../components/ThemeToggle";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import SmsCodeCaptchaDialog from "./SmsCodeCaptchaDialog";

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { getCaptcha, sendSmsCode, resetPassword } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsCaptchaOpen, setSmsCaptchaOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ phone: "", code: "", newPassword: "" });
  const [errors, setErrors] = useState<{ phone?: string; code?: string; newPassword?: string; submit?: string; captcha?: string }>({});

  const validateForm = () => {
    const nextErrors: typeof errors = {};
    if (!isValidPhone(formData.phone)) nextErrors.phone = t("forgotPassword.phoneInvalid");
    if (!formData.code.trim()) nextErrors.code = t("forgotPassword.codeRequired");
    if (!formData.newPassword) {
      nextErrors.newPassword = t("forgotPassword.passwordRequired");
    } else if (formData.newPassword.length < 6) {
      nextErrors.newPassword = t("forgotPassword.passwordTooShort");
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await resetPassword({
        phone: formData.phone.trim(),
        code: formData.code.trim(),
        newPassword: formData.newPassword,
      });
      navigate("/login", { replace: true });
    } catch (error: any) {
      setErrors({ submit: error.response?.data?.error || t("forgotPassword.failed") });
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!isValidPhone(formData.phone)) {
      setErrors({ phone: t("forgotPassword.phoneInvalid") });
      return;
    }
    setSmsCaptchaOpen(true);
  };

  const handleConfirmSendCode = async ({ captchaId, captchaCode }: { captchaId: string; captchaCode: string }) => {
    setSendingCode(true);
    try {
      await sendSmsCode({
        phone: formData.phone.trim(),
        purpose: SmsCodePurpose.RESET_PASSWORD,
        captchaId,
        captchaCode,
      });
      startCountdown(setCountdown);
    } finally {
      setSendingCode(false);
    }
  };

  const features = [
    {
      icon: <ShieldCheck className="h-6 w-6" strokeWidth={2.2} />,
      title: t("forgotPassword.featureTitle"),
      description: t("forgotPassword.featureDescription"),
    },
  ];

  const inputCls = "h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10";

  return (
    <AuthShell
      appName={appConfig.name}
      appDescription={appConfig.description}
      title={t("forgotPassword.title")}
      subtitle={t("forgotPassword.subtitle")}
      features={features}
      homeAction={
        <button
          type="button"
          onClick={() => { navigate("/"); }}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-border-subtle bg-bg-surface/90 px-3 text-sm font-medium text-text-secondary shadow-sm backdrop-blur transition-colors hover:text-text-primary lg:border-white/60 lg:bg-white/10 lg:text-white lg:hover:bg-white/20 lg:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("forgotPassword.backHome")}
        </button>
      }
      topBar={
        <>
          <div className="w-10"><ThemeToggle compact /></div>
          <div className="h-6 w-px bg-border-subtle" />
          <div className="w-10"><LanguageSwitcher compact /></div>
        </>
      }
      brandMark={
        <div className="grid grid-cols-2 gap-1.5">
          {[0, 1, 2, 3].map((cell) => (
            <div key={cell} className="h-[21px] w-[21px] rounded-[3px] border-[2.5px] border-white" />
          ))}
        </div>
      }
      brandActionLabel={t("forgotPassword.backHome")}
      onBrandClick={() => { navigate("/"); }}
      supportMeta={t("forgotPassword.hint")}
      footerAction={
        <button type="button" onClick={() => navigate("/login")} className="inline-flex items-center gap-1 font-medium">
          <ArrowLeft className="h-4 w-4" />
          {t("forgotPassword.backToLogin")}
        </button>
      }
      form={() => (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium text-text-primary">{t("forgotPassword.phone")}</label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={(event) => {
                  setFormData({ ...formData, phone: event.target.value });
                  setErrors({ ...errors, phone: undefined });
                }}
                placeholder={t("forgotPassword.phonePlaceholder")}
                className={`${inputCls} ${errors.phone ? "border-danger" : "border-border-default"}`}
              />
            </div>
            {errors.phone ? <p className="text-sm text-danger">{errors.phone}</p> : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="code" className="block text-sm font-medium text-text-primary">{t("forgotPassword.code")}</label>
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <MessageSquareText className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={formData.code}
                  onChange={(event) => {
                    setFormData({ ...formData, code: event.target.value });
                    setErrors({ ...errors, code: undefined });
                  }}
                  placeholder={t("forgotPassword.codePlaceholder")}
                  className={`${inputCls} ${errors.code ? "border-danger" : "border-border-default"}`}
                />
              </div>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={sendingCode || countdown > 0}
                className="h-11 w-[112px] shrink-0 rounded-lg border border-border-default bg-bg-form text-sm font-medium text-text-primary transition-colors hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
              >
                {countdown > 0 ? `${countdown}s` : sendingCode ? t("forgotPassword.sendingCode") : t("forgotPassword.sendCode")}
              </button>
            </div>
            {errors.code ? <p className="text-sm text-danger">{errors.code}</p> : null}
          </div>

          <SmsCodeCaptchaDialog
            isOpen={smsCaptchaOpen}
            onClose={() => setSmsCaptchaOpen(false)}
            getCaptcha={getCaptcha}
            onConfirm={handleConfirmSendCode}
            title={t("forgotPassword.smsCaptchaTitle")}
            description={t("forgotPassword.smsCaptchaDescription")}
            label={t("forgotPassword.captcha")}
            placeholder={t("forgotPassword.captchaPlaceholder")}
            refreshLabel={t("forgotPassword.refreshCaptcha")}
            submitLabel={t("forgotPassword.sendCode")}
            submittingLabel={t("forgotPassword.sendingCode")}
            cancelLabel={t("forgotPassword.cancel")}
            requiredMessage={t("forgotPassword.captchaRequired")}
            failedMessage={t("forgotPassword.codeSendFailed")}
          />

          <div className="space-y-2">
            <label htmlFor="newPassword" className="block text-sm font-medium text-text-primary">{t("forgotPassword.newPassword")}</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="newPassword"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={formData.newPassword}
                onChange={(event) => {
                  setFormData({ ...formData, newPassword: event.target.value });
                  setErrors({ ...errors, newPassword: undefined });
                }}
                placeholder={t("forgotPassword.passwordPlaceholder")}
                className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-11 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                  errors.newPassword ? "border-danger" : "border-border-default"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-colors hover:text-text-primary"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.newPassword ? <p className="text-sm text-danger">{errors.newPassword}</p> : null}
          </div>

          {errors.submit ? <p className="text-sm text-danger">{errors.submit}</p> : null}

          <button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-lg bg-action-primary text-sm font-semibold text-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
          </button>
        </form>
      )}
    />
  );
};

function isValidPhone(phone: string): boolean {
  return /^1[3-9]\d{9}$/.test(phone.trim());
}

function startCountdown(setCountdown: React.Dispatch<React.SetStateAction<number>>) {
  setCountdown(60);
  const timer = window.setInterval(() => {
    setCountdown((value) => {
      if (value <= 1) {
        window.clearInterval(timer);
        return 0;
      }
      return value - 1;
    });
  }, 1000);
}

export default ForgotPasswordPage;
